import ErrorPopup from '@components/ErrorPopup'
import TokenAvatar from '@components/TokenAvatar'
import Touchable from '@components/Touchable'
import usePreviousValue from '@hooks/usePreviousValue'
import EthereumIcon from '@plug-core/assets/icons/eth.svg'
import { XTC_CANISTER_ID } from '@plug-core/constants/canisterIds'
import useConfig from '@plug-core/hooks/useConfig'
import useWallet from '@plug-core/hooks/useWallet'
import { Scope } from '@plug-core/interfaces/permissions'
import { logger } from '@plug-core/logger'
import { initializeMessenger } from '@plug-core/messengers'
import { useNotificationWindows, usePendingRequests } from '@plug-core/state'
import { shortAddress } from '@plug-core/utils/shortAddress'
import {
  Avatar,
  Banner,
  Button,
  Chip,
  Divider,
  Heading,
  ListGroup,
  ListRow,
  Surface,
  Text,
} from '@ui'
import { ROUTES } from '@utils/routes'
import { isExternalPopup } from '@utils/windows'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import CanisterCall from './components/CanisterCall'
import Transfer from './components/Transfer'
import TransferEth from './components/TransferEth'

const backgroundMessenger = initializeMessenger({ connect: 'background' })

interface CanisterMetadata {
  url: string
  name: string
  description: string
  version: number
  logo_url: string
  canisterId: string
}

function hexToText(hexString: string): string {
  hexString = hexString.startsWith('0x') ? hexString.slice(2) : hexString

  return decodeURIComponent(hexString.replace(/\s+/g, '').replace(/[0-9a-f]{2}/g, '%$&'))
}

const SectionLabel = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 py-2">
    <Divider className="flex-1" />
    <Chip>{label}</Chip>
    <Divider className="flex-1" />
  </div>
)

const CanisterRow = ({ canisterId }: { canisterId: string }) => {
  const { nfts, tokens } = useConfig()

  const nft = nfts.find((nft) => nft.address === canisterId)
  const token = tokens.find((token) => token.address === canisterId)
  const logo = nft?.logo_url || token?.logo
  const name = nft?.name || token?.name

  return (
    <ListRow
      dense
      leading={<TokenAvatar size={32} logo={logo} />}
      primary={name}
      secondary={
        <Text as="span" variant="mono" breakAll className="block text-[13px] whitespace-normal">
          {canisterId}
        </Text>
      }
    />
  )
}

const Authorize = () => {
  const { pendingRequests, removePendingRequest } = usePendingRequests()
  const { notificationWindows } = useNotificationWindows()
  const [alertVisible, setAlertVisible] = useState(false)

  const {
    wallet: { name, principalId, ethAddress },
  } = useWallet()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const filteredRequests = useMemo(() => {
    return isExternalPopup
      ? pendingRequests.filter((request) => {
          return request.meta?.sender?.tab?.id === Number(window.location.search.split('tabId=')[1])
        })
      : pendingRequests
  }, [pendingRequests])

  const [pendingRequest, setPendingRequest] = useState(
    filteredRequests[filteredRequests.length - 1]
  )

  const prevPending = usePreviousValue(pendingRequest)

  const handleRequestAction = useCallback(() => {
    const nextPendingRequests = filteredRequests.filter((r) => r.id !== pendingRequest.id)
    removePendingRequest(pendingRequest.id)
    setPendingRequest(nextPendingRequests[nextPendingRequests.length - 1])
  }, [
    filteredRequests,
    pendingRequest?.id,
    pendingRequest?.meta?.sender?.tab?.id,
    notificationWindows,
    pendingRequests.length,
  ])

  useEffect(() => {
    const notificationWindow =
      notificationWindows[Number(prevPending?.meta?.sender?.tab?.id)?.toString()]

    if (filteredRequests.length === 0) {
      // Close tab with timeout - this allows requests that income instantly be processed
      // without rejected with Tab Closed
      // New instant incomming request will clear this timeout

      const timeout = setTimeout(async () => {
        navigate(ROUTES.HOME)
        if (notificationWindow?.id) {
          try {
            await chrome.windows.remove(notificationWindow.id)
          } catch (e) {
            logger.debug('Failed to close window', { originalError: e })
          }
        }
      }, 200)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [filteredRequests, prevPending])

  const approveRequest = useCallback(() => {
    backgroundMessenger.send(`message:${pendingRequest.id}`, true)
    handleRequestAction()
  }, [handleRequestAction, pendingRequest?.id])

  const rejectRequest = useCallback(() => {
    backgroundMessenger.send(`message:${pendingRequest.id}`, null)
    handleRequestAction()
  }, [handleRequestAction, pendingRequest?.id])

  useEffect(() => {
    if (pendingRequest?.method === 'unlock') approveRequest()
  }, [pendingRequest])

  const renderRequestPermissions = () => {
    if (!pendingRequest?.meta) return

    const hasGlobalDelegationRequest = !!pendingRequest.params?.scopes.find(
      (scope: Scope) => scope.method === 'icrc34_get_global_delegation'
    )

    return (
      <div className="flex flex-col">
        <SectionLabel label="Permissions" />
        <div className="flex flex-col gap-2 px-4">
          {hasGlobalDelegationRequest && (
            <Touchable onClick={() => setAlertVisible(true)}>
              <Banner tone="warning" title={t('authorizationRequest.globalDelegationWarningTitle')}>
                <Text as="span" variant="caption">
                  {t('authorizationRequest.readMore')}
                </Text>
              </Banner>
            </Touchable>
          )}
          {pendingRequest.params.scopes?.map((scope: Scope, index: number) => (
            <Surface padding="sm" className="mx-2 flex flex-col gap-2" key={index}>
              <Text weight="bold" className="ml-2">
                {`${index + 1}. ${t(`authorizationRequest.${scope.method}`)}`}
              </Text>
              {scope.targets && (
                <>
                  <Divider />
                  <Text variant="body-s" tone="fg-2" className="ml-2">
                    For canisters:
                  </Text>
                  <ListGroup card={false}>
                    {scope.targets.map((target) => (
                      <CanisterRow canisterId={target} key={target} />
                    ))}
                  </ListGroup>
                </>
              )}
            </Surface>
          ))}
        </div>
      </div>
    )
  }

  const renderPersonalSign = (message: string) => {
    return (
      <div className="flex flex-col gap-6">
        <SectionLabel label="Sign message" />
        <div className="flex flex-col gap-4">
          <Surface className="flex flex-col gap-2">
            <Text weight="bold">{t('approveTransaction.personal_sign.title')}</Text>
            <Text variant="mono" breakAll>
              {message}
            </Text>
          </Surface>
          <Surface className="flex flex-col gap-1">
            <Text weight="bold" className="mb-1">
              {t('approveTransaction.personalSign.additionalInformation')}
            </Text>
            <Text variant="body-s" tone="fg-2">
              • {t('approveTransaction.personal_sign.gasInfo')}
            </Text>
            <Text variant="body-s" tone="fg-2">
              • {t('approveTransaction.personal_sign.warning')}
            </Text>
          </Surface>
        </div>
      </div>
    )
  }

  const renderDappInfo = () => {
    if (!pendingRequest?.meta) return
    const { tab, url } = pendingRequest.meta.sender

    const dappUrl = new URL(url || '')

    if (
      pendingRequest?.method == 'eth_accounts' ||
      pendingRequest?.params?.scopes?.some((scope: Scope) => scope.method === 'eth_accounts')
    ) {
      return (
        <>
          <div className="flex items-center gap-4 px-4 py-2">
            <Avatar size={50} src={tab?.favIconUrl} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Heading level={4}>
                {t(`${pendingRequest?.params?.method || pendingRequest?.method}`)}
              </Heading>
              <Text variant="body-s" tone="fg-2" breakAll>
                {dappUrl.host}
              </Text>
            </div>
          </div>
          <div className="flex flex-col gap-4 px-4">
            <div className="flex items-center gap-2">
              <Avatar src={EthereumIcon} size={24} />
              <Text tone="fg-2" weight="regular">
                {t(`Ethereum Mainnet`)}
              </Text>
            </div>
            <ListGroup>
              <ListRow
                primary="Account"
                trailing={
                  <Text as="span" variant="mono">
                    {shortAddress(ethAddress || '')}
                  </Text>
                }
              />
            </ListGroup>
          </div>
        </>
      )
    }

    return (
      <>
        <div className="flex items-center gap-4 px-4 py-2">
          <Avatar size={50} src={tab?.favIconUrl} />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Heading level={4}>{t(`approveTransaction.${pendingRequest?.method}.title`)}</Heading>
            <Text variant="body-s" tone="fg-2" breakAll>
              {dappUrl.host}
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-4 px-4">
          <Text tone="fg-2">{t(`approveTransaction.${pendingRequest?.method}.description`)}</Text>
          <ListGroup>
            <ListRow
              primary="Account"
              trailing={
                <Text as="span" variant={name ? 'body-s' : 'mono'}>
                  {name || shortAddress(principalId)}
                </Text>
              }
            />
          </ListGroup>
        </div>
      </>
    )
  }

  const renderCanisterCall = () => {
    const { canisterId, method, arg } = pendingRequest.params

    return <CanisterCall method={method} canisterId={canisterId} arg={arg} />
  }

  const renderDelegationPermissions = () => (
    <div className="mt-4 flex flex-col px-4">
      {pendingRequest.params?.targets?.length > 0 && (
        <>
          <Touchable onClick={() => setAlertVisible(true)}>
            <Banner tone="warning" title={t('authorizationRequest.globalDelegationWarningTitle')}>
              <Text as="span" variant="caption">
                {t('authorizationRequest.readMore')}
              </Text>
            </Banner>
          </Touchable>
          <Text variant="body-s" tone="fg-2" className="mt-2">
            Target canisters:
          </Text>
        </>
      )}
      <ListGroup card={false} className="mt-1">
        {(pendingRequest.params?.targets || []).map((canister: string) => (
          <CanisterRow canisterId={canister} key={canister} />
        ))}
      </ListGroup>
    </div>
  )

  const renderRequestEthTransfer = () => {
    return <TransferEth transfer={pendingRequest.params.transaction} />
  }

  const renderRequestTransfer = () => {
    return (
      <Transfer
        transferParams={pendingRequest.params.transferParams}
        tokenCanisterId={pendingRequest.params.tokenCanisterId}
      />
    )
  }

  const renderBurnRequest = () => {
    return (
      <Transfer
        transferParams={pendingRequest.params.burnParams}
        tokenCanisterId={XTC_CANISTER_ID}
      />
    )
  }

  const renderActionBody = () => {
    switch (pendingRequest?.method) {
      case 'wallet_requestPermissions':
      case 'icrc25_request_permissions':
        return renderRequestPermissions()
      case 'icrc34_delegation':
        return renderDelegationPermissions()
      case 'icrc49_call_canister':
      case 'icrc49_call_canister_compatibility':
        return renderCanisterCall()
      case 'request_transfer':
        return renderRequestTransfer()
      case 'request_burn_xtc':
        return renderBurnRequest()
      case 'eth_accounts':
        switch (pendingRequest.params.method) {
          case 'eth_sendTransaction':
            return renderRequestEthTransfer()
          case 'personal_sign':
            return renderPersonalSign(hexToText(pendingRequest.params.message))
          default:
            return null
        }
    }
  }

  return (
    <div className="flex h-full flex-col">
      {renderDappInfo()}
      <div className="flex flex-1 flex-col overflow-scroll">{renderActionBody()}</div>
      <div className="flex flex-col gap-4 border-t border-hairline bg-surface-2 p-4">
        <Button onClick={approveRequest} variant="primary" fullWidth disabled={!pendingRequest}>
          Approve
        </Button>
        <Button
          onClick={() => rejectRequest()}
          variant="secondary"
          fullWidth
          disabled={!pendingRequest}
        >
          Reject
        </Button>
      </div>
      <ErrorPopup
        title="Warning!"
        description={t('authorizationRequest.globalDelegationWarning')}
        visible={alertVisible}
        onDismiss={() => setAlertVisible(false)}
      />
    </div>
  )
}

export default Authorize
